import { Pagination } from "~/types/pagination";
import {
  getRequest,
  putRequest,
  download,
  downloadWithSavePicker,
  postRequest,
} from "~/apis/requests/useApiCall";
import { definePiniaStore, exportToExcel } from "#imports";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { exportToExcel } from "~/utils/dataTableHelper";

import { Queue } from "~/projects/tele-sales/types/queue";
import { TeleSalesCustomer } from "~/projects/tele-sales/types/TeleSalesCustomer";
export const useTeleSalesStore = definePiniaStore("teleSalesStore", {
  state: () => ({
    _loading: true,
    _teleSalesCustomersTableData: [] as Array<Queue>,
    _teleSalesCustomersLoading: true,
    _tableData: [] as Array<Queue>,
    _filterData: {
      agent_id: useAuthStore().user?.id || null,
    } as Partial<Queue>,
    _teleSalesCustomersPagination: {} as Pagination,
    _selectedTeleSalesCustomer: {} as TeleSalesCustomer,
    _selectedTeleSaleForEdit: {} as Queue,
    multipleSelection: [] as Array<TeleSalesCustomer>,
    _showTeleSaleEditModal: false,
    _showAssignAgentModal: false,
    showMergeModal: false,
    showExcScenariosModal: false,
    showLogTimeModal: false,
    showTeleSaleCreateModal: false,
    _teleSalesLoading: false,
    showBulkUpload: false,
    _showSwapAssignAgentModal: false,
  }),
  actions: {
    async getTeleSalesCustomers(pageNo = 1, sortBy = "id", ascending = false) {
      this._teleSalesCustomersLoading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `tele-sales/telesales?page=${pageNo}&sort_column=${sortBy}&order=${sortDir}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Queue>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._teleSalesCustomersTableData = data;
        this.$state._teleSalesCustomersPagination = pagination;
        this._teleSalesCustomersLoading = false;
      }
    },
    async getTeleSalesCustomer(teleSalesCustomerId: number) {
      const { response, error } = await getRequest<Queue>(
        `tele-sales/telesales/${teleSalesCustomerId}`,
      );
      if (!error) {
        this.$state._selectedTeleSalesCustomer = response.data;
      }
    },
    async teleSalesCustomersBulkInsert(
      file: File,
      userId: number | null = null,
      categoryId: number | null = null,
      deadline: string | null = null,
      updateExistingProfileName: boolean | null = null,
    ) {
      const { response, error } = await postRequest<boolean>(
        "tele-sales/telesales/bulk",
        {
          file,
          agent_id: userId,
          cat_id: categoryId,
          deadline,
          created_by: useAuthStore().user?.id,
          // Omitted when null so the backend falls back to the saved setting.
          ...(updateExistingProfileName === null
            ? {}
            : {
                update_existing_profile_name: updateExistingProfileName ? 1 : 0,
              }),
        },
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!error) {
        return response.data;
      }

      return null;
    },
    async getTeleSalesImportSettings() {
      const { response, error } = await getRequest<any>(
        "tele-sales/telesales/import-settings",
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async updateTeleSalesImportSettings(data: any) {
      const { response, error } = await putRequest<any>(
        "tele-sales/telesales/import-settings",
        data,
        true,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return { response, error };
    },
    async downloadSampleExcelFile() {
      await download(
        "tele-sales/telesales/bulk/sample-file",
        "sample_tele_sales_customers_bulk_update_file.xlsx",
      );
    },
    async bulkAssign(agent) {
      const teleSalesCustomersIds = this.multipleSelection.map(
        (t: Queue) => t.id,
      );

      const { response, error } = await putRequest<boolean>(
        "tele-sales/telesales/bulk-assign",
        {
          tele_sales_customers_ids: teleSalesCustomersIds,
          assigned_agent_id: agent,
        },
      );
      if (!error) {
        await this.getTeleSalesCustomers();
        return response.data;
      }
    },
    async bulkSwapAssign(obj) {
      const { response, error } = await putRequest<boolean>(
        "tele-sales/telesales/bulk-swap-assign",
        obj,
      );
      if (!error) {
        await this.getTeleSalesCustomers();
        return response.data;
      }
    },
    async exportTeleSalesLogisticReport(date: string) {
      const url = `tele-sales/telesales/export-tele-sale-report?date=${date}`;

      const fileName = `${"tele_sales_report"}`;

      await downloadWithSavePicker(url, fileName);

      // const { response, error } = await getRequest<Queue[]>(url);
      // if (!error) {
      //   return exportToExcel<Queue>(response.data, "tele_sales_report");
      // }
    },

    setFilterData(filterData: object) {
      this._filterData = filterData;
    },

    async updateTeleSale(queue: Queue) {
      this._teleSalesLoading = true;
      const { response, error } = await putRequest<Queue>(
        `tele-sales/telesales/${queue.id}`,
        queue,
      );
      if (!error) {
        this._teleSalesLoading = false;
        return response.data;
      }
      return null;
    },
    async createTeleSale(
      teleSale: Partial<Queue>,
      teleSalesCustomer: TeleSalesCustomer,
    ) {
      const { response, error } = await postRequest<Partial<Queue>>(
        `tele-sales/telesales/${teleSalesCustomer.id}/queue`,
        teleSale,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    setSelectedTeleSalesCustomerForEdit(teleSalesCustomer: TeleSalesCustomer) {
      this._selectedTeleSalesCustomer = teleSalesCustomer;
    },

    // async exportTable() {
    //   let url = `desk/tickets?excel=true`;
    //   for (const [key, value] of Object.entries(this._filterData)) {
    //     if (!value) continue;
    //     url += `&${key}=${value}`;
    //   }
    //   const { response, error } = await getRequest<Ticket[]>(url);
    //   if (!error) {
    //     exportToExcel<Ticket>(response.data, "tickets");
    //   }
    // },

    setBulkUploadModalVisibility(visible: boolean) {
      this.showBulkUpload = visible;
    },
    setTeleSaleEditModalVisibility(visible: boolean) {
      this._showTeleSaleEditModal = visible;
    },
    setShowAssignAgentModalVisibility(visible: boolean) {
      this._showAssignAgentModal = visible;
    },
    setShowSwapAssignAgentModalVisibility(visible: boolean) {
      this._showSwapAssignAgentModal = visible;
    },
    setShowTeleSaleCreateModalVisibility(visible: boolean) {
      this.showTeleSaleCreateModal = visible;
    },
    setSelectedTeleSaleForEdit(queue: Queue) {
      this._selectedTeleSaleForEdit = queue;
    },
    selectionChange(selection: TeleSalesCustomer[]) {
      this.multipleSelection = selection;
    },
    setTeleSalesLoading(loading: boolean) {
      this._teleSalesLoading = loading;
    },
  },
  getters: {
    showEditOptions(state) {
      return state.multipleSelection.length > 0;
    },
    tableData: (state) => state._tableData,
    statuses: (state) => {
      return state._statuses;
    },
    showSwapAssignAgentModal: (state) => state._showSwapAssignAgentModal,
    selectedTeleSalesCustomer: (state) => state._selectedTeleSalesCustomer,
    showTeleSaleEditModal: (state) => state._showTeleSaleEditModal,
    sources: (state) => state._sources,
    priorities: (state) => state._priorities,
    teleSalesCustomersPagination: (state) =>
      state._teleSalesCustomersPagination,
    teleSalesCustomersTableData: (state) => state._teleSalesCustomersTableData,
    teleSalesCustomersLoading: (state) => state._teleSalesCustomersLoading,
    selectedTeleSaleForEdit: (state) => state._selectedTeleSaleForEdit,
    showAssignAgentModal: (state) => state._showAssignAgentModal,
    teleSalesLoading: (state) => state._teleSalesLoading,
  },
});
